import { ACCOUNT_CATEGORY, CHAIN_NAMESPACES } from "@toruslabs/base-controllers";
import { base64ToBytes } from "@toruslabs/metadata-helpers";
import {
  type AccessTokenProvider,
  Auth,
  AuthConnectionConfig,
  AuthSessionData,
  AuthUserInfo,
  BUILD_ENV_TYPE,
  HttpClient,
  LocalStorageAdapter,
  SessionStorageAdapter,
  subkey,
  WEB3AUTH_NETWORK_TYPE,
  WhiteLabelData,
} from "@web3auth/auth";
import { Mutex } from "async-mutex";
import log from "loglevel";

import useUtils from "../../composables/use-utils";
import config from "../../config";
import { SFA_KEY } from "../../utils/enums";
import type { Account, DashboardUserProject } from "../../utils/interfaces";
import { generateAddressFromPrivateKey } from "../../utils/utils";
import { CITADEL_SERVER_MAP } from "@toruslabs/constants";

const DEV_DASHBOARD_CHAIN_NAMESPACE_MAP: Record<string, string> = {
  eip155: "evm",
  solana: "solana",
  casper: "casper",
  other: "other",
};

class AuthFactory {
  private static _instance: Auth;

  private static mutex = new Mutex();

  private static cachedUserProjects: Record<string, DashboardUserProject[]> = {};

  public static async getInstance(params: {
    web3AuthClientId: string;
    web3AuthNetwork: WEB3AUTH_NETWORK_TYPE;
    sessionNamespace?: string;
    rehydrate?: boolean;
    whiteLabel?: WhiteLabelData;
    originData?: Record<string, string>;
    sessionId?: string;
    replaceUrlOnRedirect?: boolean;
    authConnectionConfig?: AuthConnectionConfig;
    idToken?: string;
    accessTokenProvider?: AccessTokenProvider;
  }): Promise<Auth> {
    if (!AuthFactory._instance || params.rehydrate) {
      const releaseLock = await this.mutex.acquire();
      // If we do this outside the lock, we might end up in a case where the session id is deleted by the older instance.
      // Consider this edge case.
      // Step 1 - Initiate the wallet service iframe and auth.init() [1st instance]. This instance has a stale sessionId in the storage.
      // Step 2 - Login with session Id, this will set the storage with the new sessionId.
      // Step 3 - the 1st instance init finished and deletes the sessionId from the storage.
      // Step 4 - LoginWithSessionId  initiates the 2nd instance with no sessionId in the storage, hence failing to connect.
      if (params.sessionId) {
        await useUtils().setSessionIdToStorage(params.sessionId);
      }

      const authStorage = useUtils().storageUtils.value.storageType === "local" ? new LocalStorageAdapter() : new SessionStorageAdapter();

      try {
        const instance = new Auth({
          clientId: params.web3AuthClientId,
          network: params.web3AuthNetwork,
          whiteLabel: params.whiteLabel,
          redirectUrl: `${config.baseRoute}end`,
          replaceUrlOnRedirect: params.replaceUrlOnRedirect,
          uxMode: "redirect",
          buildEnv: config.buildEnv as BUILD_ENV_TYPE,
          sessionNamespace: params.sessionNamespace,
          originData: params.originData,
          storage: {
            sessionId: authStorage,
            refreshToken: authStorage,
            accessToken: authStorage,
            idToken: authStorage,
          },
          ...(params.authConnectionConfig ? { authConnectionConfig: params.authConnectionConfig } : {}),
          ...(params.accessTokenProvider ? { accessTokenProvider: params.accessTokenProvider } : {}),
        });
        await instance.init();
        if (params.idToken) {
          await instance.authSessionManager.setTokens({ idToken: params.idToken });
        }
        AuthFactory._instance = instance;
      } catch (error) {
        log.error(error, "Unable to create auth service instance");
        throw error;
      } finally {
        releaseLock();
      }
    }
    return AuthFactory._instance;
  }

  public static async getIdToken(): Promise<string> {
    return AuthFactory._instance.authSessionManager.getIdToken();
  }

  public static async getAccessToken(): Promise<string> {
    return AuthFactory._instance.getAccessToken();
  }

  public static async getUserInfo(): Promise<AuthUserInfo> {
    return AuthFactory._instance.getUserInfo();
  }

  public static async getKeysInfo() {
    const { sessionNamespace } = AuthFactory._instance.options;
    const {
      oAuthPrivateKey: unpaddedOAuthPrivateKey,
      privKey: unpaddedPrivKey,
      walletKey: unpaddedWalletKey,
      tKey: unpaddedTKey,
      signatures,
      coreKitKey: unpaddedCoreKitKey,
      useCoreKitKey,
    } = AuthFactory._instance.state;

    const oAuthPrivateKey = unpaddedOAuthPrivateKey ? unpaddedOAuthPrivateKey.padStart(64, "0") : "";
    const privKey = unpaddedPrivKey ? unpaddedPrivKey.padStart(64, "0") : "";
    const walletKey = unpaddedWalletKey ? unpaddedWalletKey.padStart(64, "0") : "";
    const tKey = unpaddedTKey ? unpaddedTKey.padStart(64, "0") : "";
    const coreKitKey = unpaddedCoreKitKey ? unpaddedCoreKitKey.padStart(64, "0") : "";

    // keys
    const keys: Account[] = [];

    // To use in torus wallet sense, we only use wallet key or tkey
    if (privKey && !walletKey) {
      // this is for sfa sdks only.
      if (sessionNamespace === SFA_KEY) {
        keys.push({
          privKey,
          accountType: signatures?.length > 0 ? ACCOUNT_CATEGORY.SFA : ACCOUNT_CATEGORY.EXTERNAL,
          address: generateAddressFromPrivateKey(privKey),
        });
      } else {
        // this is for pnp sdks.
        const finalPrivkey = useCoreKitKey ? coreKitKey : privKey;
        keys.push({
          privKey: finalPrivkey,
          accountType: useCoreKitKey ? ACCOUNT_CATEGORY.THRESHOLD : ACCOUNT_CATEGORY.APP_SCOPED,
          address: generateAddressFromPrivateKey(finalPrivkey),
        });
      }
    }

    if (walletKey) {
      const userInfo = await AuthFactory.getUserInfo();
      const hasIdToken = userInfo.idToken;
      const ethAddress = generateAddressFromPrivateKey(walletKey);
      const accountType = walletKey === tKey ? ACCOUNT_CATEGORY.THRESHOLD : ACCOUNT_CATEGORY.NORMAL;
      keys.push({
        privKey: walletKey,
        accountType: hasIdToken ? accountType : ACCOUNT_CATEGORY.EXTERNAL,
        address: ethAddress,
      });

      // never use tkey if walletKey is not present
      // we use app-scoped key instead
      if (tKey && tKey !== walletKey) {
        keys.push({
          privKey: tKey,
          accountType: hasIdToken ? ACCOUNT_CATEGORY.THRESHOLD : ACCOUNT_CATEGORY.EXTERNAL,
          address: generateAddressFromPrivateKey(tKey),
        });
      }
    }

    let postboxKey: Account;
    if (oAuthPrivateKey) {
      postboxKey = {
        privKey: oAuthPrivateKey,
        address: generateAddressFromPrivateKey(oAuthPrivateKey),
        accountType: ACCOUNT_CATEGORY.NORMAL,
      };
    }

    return { keys, postboxKey, signatures };
  }

  public static isUserSkippedMfa(keys: Account[]): boolean {
    // if v1 user doesn't have threshold key and only has normal key, then they skipped mfa
    return (
      !keys.some((key) => key.accountType === ACCOUNT_CATEGORY.THRESHOLD || key.accountType === ACCOUNT_CATEGORY.APP_SCOPED) &&
      keys.some((key) => key.accountType === ACCOUNT_CATEGORY.NORMAL)
    );
  }

  public static async getUserDapps({ postboxKey, chainNamespace = config.defaultChainNamespace, httpClient }: { postboxKey: Account, chainNamespace?: string, httpClient: HttpClient }) {
    const userDapps: Record<string, string> = {};
    const keys: Account[] = [];
    const { tKey, oAuthPrivateKey } = AuthFactory._instance.state;

    if (tKey && oAuthPrivateKey) {
      try {
        const cacheKey = `${postboxKey.address}-${chainNamespace}`;
        let userProjects = AuthFactory.cachedUserProjects[cacheKey];
        if (!userProjects) {
          // current developer dashboard has only evm, solana, casper right now so we need to map the chainNamespace to match
          const queryChainNamespace = DEV_DASHBOARD_CHAIN_NAMESPACE_MAP[chainNamespace] || chainNamespace;
          const response = await httpClient.get<{ user_projects: DashboardUserProject[] }>(
            `${CITADEL_SERVER_MAP[config.buildEnv as BUILD_ENV_TYPE]}/v1/user/projects?chain_namespace=${queryChainNamespace}`,
            { authenticated: true }
          );
          log.info("got User projects from developer dashboard");
          userProjects = response.user_projects ?? [];
          AuthFactory.cachedUserProjects[cacheKey] = userProjects;
        }

        if (chainNamespace === CHAIN_NAMESPACES.SOLANA) {
          // Legacy solana wallet uses subkey derived from solana wallet project id, so we need to add it to the user projects
          userProjects.push({
            project_id: config.legacySolanaWalletProjectId,
            last_login: new Date().toISOString(),
            name: "Solana Torus Wallet",
            hostname: "solana.tor.us",
          });
        }
        userProjects.sort((a, b) => (a.last_login < b.last_login ? 1 : -1));
        userProjects.forEach((project) => {
          const subKey = subkey(tKey, base64ToBytes(project.project_id));
          const subAddress = generateAddressFromPrivateKey(subKey);
          userDapps[subAddress] = project.hostname ? `${project.name} (${project.hostname})` : project.name;
          keys.push({ address: subAddress, privKey: subKey.padStart(64, "0"), accountType: ACCOUNT_CATEGORY.APP_SCOPED });
        });
      } catch (error) {
        log.error("fail to derive app-scoped keys", error);
      }
    }
    return { userDapps, keys };
  }

  public static async getAuthDashboardDapps() {
    const userDapps: Record<string, string> = {};
    const keys: Map<string, Account> = new Map();
    // connected apps are list of accounts from dapps provided by auth dashboard
    const { connectedApps, tKey, oAuthPrivateKey } = AuthFactory._instance.state as AuthSessionData & {
      connectedApps: { clientId: string; appUrl: string; appName: string; dappId: number }[];
    };
    if (connectedApps && tKey && oAuthPrivateKey) {
      connectedApps.forEach((app) => {
        const subKey = subkey(tKey, base64ToBytes(app.clientId));
        const subAddress = generateAddressFromPrivateKey(subKey);
        userDapps[subAddress] = app.appUrl ? `${app.appName || app.appUrl} (${app.appUrl})` : app.appName;
        if (!keys.has(subAddress)) {
          keys.set(subAddress, {
            address: subAddress,
            privKey: subKey.padStart(64, "0"),
            accountType: ACCOUNT_CATEGORY.APP_SCOPED,
            dappIds: [app.dappId],
          });
        } else {
          const existingKey = keys.get(subAddress);
          if (existingKey) {
            existingKey.dappIds.push(app.dappId);
          }
        }
      });
    }
    return { userDapps, keys: Array.from(keys.values()) };
  }

  public static async invalidateSession() {
    await AuthFactory._instance.logout();
  }
}
export default AuthFactory;
